import { APP_INITIALIZER, inject, NgModule } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
  RouterModule,
  Routes
} from '@angular/router';
import { filter, firstValueFrom, isObservable, take } from 'rxjs';
import { DefaultLayoutComponent } from '@core/layouts/default-layout/default-layout.component';
import {
  LocalizeParser,
  LocalizeRouterModule,
  LocalizeRouterService,
  LocalizeRouterSettings,
  ManualParserLoader
} from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { PlatformService } from '@core/services/platform.service';
import { SelectivePreloadStrategy } from '@core/selective-preload.strategy';

/** Redirects here */
export const redirectGuard = () => {
  const ps = inject(PlatformService);
  if (!ps.isBrowser) {
    return true;
  }
  const router = inject(Router);
  const localizedService = inject(LocalizeRouterService);
  const urlSearchParams: any = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const redirects: any = {
    '/error': '/error',
    '/login': '/member/login',
    '/login/reset': '/member/reset-password',
    '/dash/home': '/member/dashboard',
    '/dash/profile': '/member/profile',
    '/dash/subscription': '/member/subscription',
    '/dash/accounts': '/member/accounts',
    '/dash/templates': '/member/templates',
    '/dash/apps': '/member/apps',
    '/dash/support': '/member/support',
    '/dash/support/tickets': '/member/support',
  }
  const newRoute = (newRoute: string) => { return router.navigate([localizedService.translateRoute(newRoute)], { queryParams: params }); }
  return redirects[location.pathname] ? newRoute(redirects[location.pathname]) : true;
}


const routes: Routes = [
  {
    path: '',
    component: DefaultLayoutComponent,
    canActivate: [],
    children: [
      { path: '', redirectTo: '/home', pathMatch: 'full' },
      // `preload: true` marks the public conversion path — those chunks are fetched
      // while the browser is idle, everything else stays on demand (see SelectivePreloadStrategy)
      { path: 'home', loadChildren: () => import('./modules/home/home.module').then(m => m.HomeModule), data: { preload: true } },
      { path: 'features', loadChildren: () => import('./modules/features/features.module').then(m => m.FeaturesModule), data: { preload: true } },
      { path: 'ebay-lister', loadChildren: () => import('./modules/ebay-lister/ebay-lister.module').then(m => m.EbayListerModule), data: { preload: true } },
      { path: 'gpsr', loadChildren: () => import('./modules/gpsr/gpsr.module').then(m => m.GpsrModule) },
      { path: 'car-parts', loadChildren: () => import('./modules/car-parts/car-parts.module').then(m => m.CarPartsModule) },
      { path: 'download', loadChildren: () => import('./modules/download/download.module').then(m => m.DownloadModule), data: { preload: true } },
      { path: 'pricing', loadChildren: () => import('./modules/pricing/pricing.module').then(m => m.PricingModule), data: { preload: true } },
      { path: 'help', loadChildren: () => import('./modules/help-pages/help-pages.module').then(m => m.HelpPagesModule) },
      { path: 'member', loadChildren: () => import('./modules/member/member.module').then(m => m.MemberModule) },
      { path: 'register', loadChildren: () => import('./modules/register/register.module').then(m => m.RegisterModule) },
      { path: 'error', loadChildren: () => import('./modules/error/error.module').then(m => m.ErrorModule) },
      { path: 'legal', loadChildren: () => import('./modules/legal/legal.module').then(m => m.LegalModule) },
      { path: 'contact', loadChildren: () => import('./modules/contact/contact.module').then(m => m.ContactModule), data: { preload: true } },
      { path: 'forum', loadChildren: () => import('./modules/community/community.module').then(m => m.CommunityModule), data: { title: '', skipRouteLocalization: true, discriminantPathKey: 'forum' } },
      { path: 'blog', loadChildren: () => import('./modules/blog/blog.module').then(m => m.BlogModule), data: { title: '', skipRouteLocalization: true, discriminantPathKey: 'blog' } },
      { path: 'faq', loadChildren: () => import('./modules/faq/faq.module').then(m => m.FaqModule), data: { title: '', skipRouteLocalization: true, discriminantPathKey: 'faq' } },
      { path: 'ssr-loading', loadChildren: () => import('./modules/ssr-loading/ssr-loading.module').then(m => m.SsrLoadingModule), data: { skipRouteLocalization: true, discriminantPathKey: 'ssr-loading' } },
    ]
  },
  { path: 'client-blog', loadChildren: () => import('./modules/blog/blog.module').then(m => m.BlogModule), data: {skipRouteLocalization: true, clientMode: true} },
  { path: 'client-help', loadChildren: () => import('./modules/help-pages/help-pages.module').then(m => m.HelpPagesModule), data: {skipRouteLocalization: true, clientMode: true} },
  { path: 'redirect', loadChildren: () => import('./modules/redirect/redirect.module').then(m => m.RedirectModule), data: {skipRouteLocalization: true} },
  {
    path: '**',
    component: DefaultLayoutComponent,
    canActivate: [redirectGuard],
    children: [{ path: '**', loadChildren: () => import('./modules/page-not-found/page-not-found.module').then(m => m.PageNotFoundModule), }]
  }
];


@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'disabled',
      initialNavigation: 'disabled', // triggered manually after route localization, see initializer below
      anchorScrolling: 'enabled',
      // preload the public conversion path once the browser is idle; the member area,
      // forum (CKEditor, ~1.9 MB) and help pages stay on demand
      preloadingStrategy: SelectivePreloadStrategy
    }),
    LocalizeRouterModule.forRoot(routes, {
      parser: {
        provide: LocalizeParser,
        useFactory: (translate: TranslateService, location: any, settings: any) =>
          new ManualParserLoader(translate, location, settings, [...translate.getLangs()], 'ROUTES.'),
        deps: [TranslateService, Location, LocalizeRouterSettings]
      },
      // must stay false on Angular 21: the library's own initialNavigation hook patches
      // the removed private Router.navigationTransitions API and hangs bootstrap forever
      initialNavigation: false,
      useCachedLang: true
    })
  ],
  providers: [
    {
      // Replacement for the localize-router initialNavigation feature using public APIs:
      // wait until the parser has localized the routes, then run the initial navigation
      // and keep bootstrap blocked until it settles (required for SSR/prerendering).
      provide: APP_INITIALIZER,
      multi: true,
      useFactory: (localize: LocalizeRouterService, router: Router, translate: TranslateService) => () =>
        firstValueFrom(localize.hooks.initialized).then(() => {
          // the localize parser sets the fallback to the browser language —
          // override: missing translations always fall back to english
          translate.setFallbackLang('en');

          // ngx-translate-router v8 overrides RouterConfigLoader.loadChildren with an
          // Observable-returning version, but Angular 21 awaits it as a Promise.
          // Wrap the override so lazy route configs resolve again.
          const configLoader = (router as any).navigationTransitions?.configLoader;
          if (configLoader && !configLoader.__promiseWrapped) {
            const libLoadChildren = configLoader.loadChildren.bind(configLoader);
            configLoader.loadChildren = (inj: unknown, route: unknown) => {
              const res = libLoadChildren(inj, route);
              return isObservable(res) ? firstValueFrom(res) : res;
            };
            configLoader.__promiseWrapped = true;
          }

          const settled = firstValueFrom(router.events.pipe(
            filter(e => e instanceof NavigationEnd || e instanceof NavigationError
              || e instanceof NavigationCancel || e instanceof NavigationSkipped),
            take(1)
          ));
          router.initialNavigation();
          return settled;
        }),
      deps: [LocalizeRouterService, Router, TranslateService]
    }
  ],
  exports: [
    RouterModule,
  ]
})
export class AppRoutingModule { }
